import React from "react";
import type { Professional } from "../../../types";
import { Avatar, Loading } from "../../shared";
import { Icons } from "../../shared/Icons";

const AVATAR_VARIANTS: Array<"blue" | "emerald" | "purple" | "gray"> = [
  "blue",
  "emerald",
  "purple",
  "gray",
];

interface ReportsProfessionalAvatarSelectProps {
  professionals: Professional[];
  selectedProfessionalId: number | null;
  onSelectProfessional: (id: number | null) => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  hideAllOption?: boolean;
  readOnly?: boolean;
}

export const ReportsProfessionalAvatarSelect: React.FC<
  ReportsProfessionalAvatarSelectProps
> = ({
  professionals,
  selectedProfessionalId,
  onSelectProfessional,
  loading = false,
  disabled = false,
  className = "",
  hideAllOption = false,
  readOnly = false,
}) => {
  const isAllSelected = selectedProfessionalId == null && !hideAllOption;

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-2 ${className}`.trim()}>
        <Loading size="sm" />
      </div>
    );
  }

  if (professionals.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
        {readOnly ? "Profissional" : "Filtrar por profissional"}
      </p>
      <div className="flex flex-wrap items-start gap-2">
        {!hideAllOption && (
        <button
          type="button"
          disabled={disabled || readOnly}
          onClick={() => onSelectProfessional(null)}
          title="Todos os profissionais"
          aria-pressed={isAllSelected}
          className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 min-w-[4.5rem] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            isAllSelected
              ? "border-sky-500 bg-sky-50 dark:bg-sky-950 ring-2 ring-sky-200 dark:ring-sky-800 shadow-sm"
              : "border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700"
          }`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              isAllSelected
                ? "bg-sky-500 text-white"
                : "bg-gray-200 text-gray-600 dark:bg-slate-600 dark:text-slate-200"
            }`}
          >
            <Icons.Group sx={{ fontSize: 22 }} />
          </span>
          <span className="text-[10px] sm:text-xs font-medium text-gray-900 dark:text-white leading-tight text-center">
            Todos
          </span>
        </button>
        )}

        {professionals.map((professional, index) => {
          const selected = selectedProfessionalId === professional.id;

          return (
            <button
              key={professional.id}
              type="button"
              disabled={disabled || readOnly}
              onClick={() => onSelectProfessional(professional.id)}
              title={professional.name}
              aria-pressed={selected}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 min-w-[4.5rem] max-w-[5.5rem] transition-all disabled:opacity-50 ${
                readOnly ? "cursor-default" : "disabled:cursor-not-allowed"
              } ${
                selected
                  ? "border-sky-500 bg-sky-50 dark:bg-sky-950 ring-2 ring-sky-200 dark:ring-sky-800 shadow-sm"
                  : "border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-800 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700"
              }`}
            >
              <Avatar
                text={professional.name}
                src={professional.picture}
                size="md"
                variant={AVATAR_VARIANTS[index % AVATAR_VARIANTS.length]}
              />
              <span className="text-[10px] sm:text-xs font-medium text-gray-900 dark:text-white line-clamp-2 w-full text-center leading-tight">
                {professional.name.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
